import { useEffect, useRef, useCallback } from "react";

import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { initializePush } from "../config/push";
import apiService from "../services/api";
import { debugLog } from "../utils/debugLog";

const ReminderManager = () => {
  const navigate = useNavigate();
  const reminderTimersRef = useRef({});
  const initializedRef = useRef(false);
  const audioRef = useRef(null);
  const shownNotificationsRef = useRef(new Set()); // Track shown notifications to prevent duplicates

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio("/notification.mp3");
        audioRef.current.volume = 0.5;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((err) => {
        debugLog("[ReminderManager] Could not play sound:", err);
      });
    } catch (err) {
      debugLog("[ReminderManager] Audio error:", err);
    }
  }, []);

  // Handle reminder click - navigate to channel
  const handleReminderClick = useCallback(
    (reminder) => {
      // Store reminder data for auto-play
      sessionStorage.setItem("autoPlayReminder", JSON.stringify(reminder));

      // If already on live-tv, dispatch event to trigger channel change
      if (window.location.pathname.includes("/live-tv")) {
        window.dispatchEvent(
          new CustomEvent("playReminderChannel", { detail: reminder })
        );
      } else {
        // Navigate to live-tv
        navigate("/web-player/live-tv");
      }
    },
    [navigate]
  );

  // Schedule a reminder notification
  const scheduleReminder = useCallback(
    (reminder) => {
      const now = Date.now();
      const reminderTime =
        typeof reminder.reminderTime === "string"
          ? new Date(reminder.reminderTime).getTime()
          : reminder.reminderTime;
      const delay = reminderTime - now;

      if (delay <= 0) {
        return; // Already passed
      }

      // Clear existing timer if any
      if (reminderTimersRef.current[reminder.id]) {
        clearTimeout(reminderTimersRef.current[reminder.id]);
      }

      debugLog(
        `[ReminderManager] Scheduling reminder "${reminder.programTitle}" in ${Math.round(delay / 1000)}s`
      );

      const timerId = setTimeout(() => {
        // Prevent duplicate notifications (use reminder ID without prefix to catch both local and push)
        const notificationKey = reminder.id;

        if (shownNotificationsRef.current.has(notificationKey)) {
          debugLog(
            "[ReminderManager] Duplicate notification prevented:",
            reminder.id
          );

          return;
        }
        shownNotificationsRef.current.add(notificationKey);

        // Clear old entries after 5 minutes
        setTimeout(() => {
          shownNotificationsRef.current.delete(notificationKey);
        }, 300000);

        const minutesText =
          reminder.minutesBefore >= 60
            ? `${reminder.minutesBefore / 60} hour${reminder.minutesBefore > 60 ? "s" : ""}`
            : `${reminder.minutesBefore} minute${reminder.minutesBefore > 1 ? "s" : ""}`;

        // Play notification sound
        playNotificationSound();

        // Show slim in-app toast notification
        toast(
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              padding: "14px 20px",
            }}
          >
            {/* Channel logo */}
            {reminder.channelLogo ? (
              <img
                alt=""
                src={reminder.channelLogo}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 8,
                  objectFit: "contain",
                  background: "rgba(255,255,255,0.1)",
                  flexShrink: 0,
                }}
              />
            ) : (
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 8,
                  background: "rgba(255,255,255,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: 20 }}>📺</span>
              </div>
            )}
            {/* Text content */}
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: "600",
                  color: "white",
                  marginBottom: 4,
                }}
              >
                {reminder.programTitle}
              </div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}>
                {reminder.channelName} - Starts in {minutesText}
              </div>
            </div>
          </div>,
          {
            autoClose: 15000,
            closeOnClick: true,
            icon: false,
            className: "reminder-toast-slim",
            position: "top-center",
          }
        );

        // Also try browser notification (for when tab is in background)
        if (Notification.permission === "granted") {
          try {
            const notification = new Notification(
              `${reminder.programTitle} starting soon!`,
              {
                body: `${reminder.channelName} - Starts in ${minutesText}`,
                icon: reminder.channelLogo || "/logo192.png",
                tag: reminder.id,
                requireInteraction: true,
              }
            );

            notification.onclick = () => {
              window.focus();
              handleReminderClick(reminder);
            };
          } catch (err) {
            debugLog("[ReminderManager] Browser notification failed:", err);
          }
        }

        // Remove from local storage after firing
        removeReminderFromStorage(reminder.id);
        delete reminderTimersRef.current[reminder.id];

        // Dispatch event to notify UI that reminder has fired
        debugLog(
          "[ReminderManager] Dispatching reminderFired event for:",
          reminder.id
        );
        window.dispatchEvent(
          new CustomEvent("reminderFired", { detail: { id: reminder.id } })
        );
      }, delay);

      reminderTimersRef.current[reminder.id] = timerId;
    },
    [handleReminderClick, playNotificationSound]
  );

  // Remove reminder from localStorage
  const removeReminderFromStorage = (reminderId) => {
    const saved = localStorage.getItem("live_tv_reminders");

    if (saved) {
      const reminders = JSON.parse(saved);
      const updated = reminders.filter((r) => r.id !== reminderId);

      localStorage.setItem("live_tv_reminders", JSON.stringify(updated));
    }
  };

  // Load and schedule all reminders
  const loadAndScheduleReminders = useCallback(async () => {
    debugLog("[ReminderManager] Loading reminders...");

    let reminders = [];

    // Try to load from API first
    try {
      const response = await apiService.webPlayerGetReminders();
      const apiReminders = response?.data || [];

      if (apiReminders.length > 0) {
        reminders = apiReminders;
        // Sync to localStorage
        localStorage.setItem("live_tv_reminders", JSON.stringify(reminders));
        debugLog(
          `[ReminderManager] Loaded ${reminders.length} reminders from API`
        );
      }
    } catch (err) {
      debugLog("[ReminderManager] API not available, using localStorage");
    }

    // Fallback to localStorage
    if (reminders.length === 0) {
      const saved = localStorage.getItem("live_tv_reminders");

      if (saved) {
        reminders = JSON.parse(saved);
        debugLog(
          `[ReminderManager] Loaded ${reminders.length} reminders from localStorage`
        );
      }
    }

    // Filter out expired reminders
    const now = Date.now();
    const validReminders = reminders.filter((r) => {
      const programStart = new Date(r.programStart).getTime();

      return programStart > now;
    });

    // Update localStorage with valid reminders only
    if (validReminders.length !== reminders.length) {
      localStorage.setItem("live_tv_reminders", JSON.stringify(validReminders));
    }

    // Schedule all valid reminders
    validReminders.forEach((reminder) => {
      scheduleReminder(reminder);
    });

    debugLog(`[ReminderManager] Scheduled ${validReminders.length} reminders`);
  }, [scheduleReminder]);

  // Initialize push and load reminders on mount
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const init = async () => {
      // Request notification permission
      if ("Notification" in window) {
        debugLog(
          "[ReminderManager] Notification permission:",
          Notification.permission
        );
        if (Notification.permission === "default") {
          const result = await Notification.requestPermission();

          debugLog("[ReminderManager] Permission request result:", result);
        }
      } else {
        debugLog("[ReminderManager] Notifications not supported");
      }

      // Initialize push notifications
      try {
        const subscription = await initializePush();

        if (subscription) {
          debugLog("[ReminderManager] Push notifications initialized");
          // Register with backend
          try {
            await apiService.webPlayerRegisterPushSubscription(subscription);
          } catch (err) {
            console.error(
              "[ReminderManager] Failed to register push subscription:",
              err
            );
          }
        }
      } catch (err) {
        console.error("[ReminderManager] Push init failed:", err);
      }

      // Load and schedule reminders
      await loadAndScheduleReminders();
    };

    init();

    // Cleanup on unmount
    return () => {
      Object.values(reminderTimersRef.current).forEach((timerId) =>
        clearTimeout(timerId)
      );
    };
  }, [loadAndScheduleReminders]);

  // Listen for storage changes (when reminders are added from other tabs/pages)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "live_tv_reminders") {
        debugLog("[ReminderManager] Reminders updated, rescheduling...");
        // Clear existing timers
        Object.values(reminderTimersRef.current).forEach((timerId) =>
          clearTimeout(timerId)
        );
        reminderTimersRef.current = {};
        // Reload and reschedule
        loadAndScheduleReminders();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => window.removeEventListener("storage", handleStorageChange);
  }, [loadAndScheduleReminders]);

  // Listen for custom event when reminder is added on same page
  useEffect(() => {
    const handleReminderAdded = (e) => {
      const reminder = e.detail;

      if (reminder) {
        debugLog(
          "[ReminderManager] New reminder added:",
          reminder.programTitle
        );
        scheduleReminder(reminder);
      }
    };

    window.addEventListener("reminderAdded", handleReminderAdded);

    return () =>
      window.removeEventListener("reminderAdded", handleReminderAdded);
  }, [scheduleReminder]);

  // Listen for push notifications from service worker
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      const { data } = event;

      if (data && data.type === "PUSH_NOTIFICATION") {
        debugLog("[ReminderManager] Push notification received:", data);

        // Prevent duplicate notifications (use reminder ID to match local notifications)
        const notificationKey = data.data?.reminderId || data.title;

        if (shownNotificationsRef.current.has(notificationKey)) {
          debugLog("[ReminderManager] Duplicate push notification prevented");

          return;
        }
        shownNotificationsRef.current.add(notificationKey);

        // Clear old entries after 5 minutes
        setTimeout(() => {
          shownNotificationsRef.current.delete(notificationKey);
        }, 300000);

        // Play notification sound
        playNotificationSound();

        // Create reminder-like object
        const reminderData = {
          channelId: data.data?.channelId,
          streamUrl: data.data?.streamUrl,
          channelName: data.body?.split(" - ")[0],
          programTitle: data.title?.replace(" starting soon!", ""),
          categoryId: data.data?.categoryId,
        };

        // Show slim in-app toast for push notifications
        toast(
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              padding: "14px 20px",
            }}
          >
            {/* Channel logo */}
            {data.icon ? (
              <img
                alt=""
                src={data.icon}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 8,
                  objectFit: "contain",
                  background: "rgba(255,255,255,0.1)",
                  flexShrink: 0,
                }}
              />
            ) : (
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 8,
                  background: "rgba(255,255,255,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: 20 }}>📺</span>
              </div>
            )}
            {/* Text content */}
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: "600",
                  color: "white",
                  marginBottom: 4,
                }}
              >
                {reminderData.programTitle}
              </div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}>
                {data.body}
              </div>
            </div>
          </div>,
          {
            autoClose: 15000,
            closeOnClick: true,
            icon: false,
            className: "reminder-toast-slim",
            position: "top-center",
          }
        );
      }
    };

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener(
        "message",
        handleServiceWorkerMessage
      );
    }

    return () => {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener(
          "message",
          handleServiceWorkerMessage
        );
      }
    };
  }, [handleReminderClick, playNotificationSound]);

  return null; // This component doesn't render anything
};

export default ReminderManager;
